import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Alert, Chip, Switch, FormControlLabel,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface Announcement {
  id: number;
  title: string;
  content: string;
  enabled: boolean;
  sort_order: number;
  updated_at?: string;
}

const emptyForm = { title: '', content: '', enabled: true, sort_order: 0 };

const AnnouncementManagement = () => {
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const fetchItems = useCallback(async () => {
    const resp = await axios.get('/api/qqmusic/announcements/all');
    if (resp.data.success) {
      setItems(resp.data.items || []);
    }
  }, []);

  useEffect(() => { fetchItems().catch(console.error); }, [fetchItems]);

  const handleOpen = (item?: Announcement) => {
    setError('');
    if (item) {
      setEditing(item);
      setFormData({
        title: item.title || '',
        content: item.content || '',
        enabled: !!item.enabled,
        sort_order: item.sort_order ?? 0,
      });
    } else {
      setEditing(null);
      setFormData(emptyForm);
    }
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.content.trim()) {
      setError('公告正文不能为空');
      return;
    }
    try {
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        enabled: formData.enabled,
        sort_order: formData.sort_order,
      };
      if (editing) {
        await axios.put(`/api/qqmusic/announcements/${editing.id}`, payload);
        setMsg('公告已更新');
      } else {
        await axios.post('/api/qqmusic/announcements', payload);
        setMsg('公告已创建');
      }
      setOpen(false);
      fetchItems();
    } catch (err: any) {
      setError(err.response?.data?.message || '保存失败');
    } finally {
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定删除这条公告？')) return;
    try {
      await axios.delete(`/api/qqmusic/announcements/${id}`);
      setMsg('公告已删除');
      fetchItems();
    } catch (err: any) {
      setMsg(err.response?.data?.message || '删除失败');
    } finally {
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '-';
    return new Date(timeStr).toLocaleString('zh-CN', { hour12: false });
  };

  const summarize = (text: string, max = 60) => {
    const s = (text || '').replace(/\s+/g, ' ').trim();
    return s.length > max ? `${s.slice(0, max)}…` : s;
  };

  return (
    <SidebarLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">公告管理</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            新建公告
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          本地总控仅展示<strong>已启用</strong>且 <strong>sort_order 最小</strong> 的一条公告；顶部 2 行预览，点击可查看全文。
        </Typography>
        {msg && <Alert severity={msg.includes('失败') ? 'error' : 'success'} sx={{ mb: 2 }}>{msg}</Alert>}
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>标题</TableCell>
                  <TableCell>正文摘要</TableCell>
                  <TableCell>启用</TableCell>
                  <TableCell>排序</TableCell>
                  <TableCell>更新时间</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      暂无公告，点击「新建公告」添加
                    </TableCell>
                  </TableRow>
                ) : items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.title || '（无标题）'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{summarize(item.content)}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.enabled ? '启用' : '停用'}
                        size="small"
                        color={item.enabled ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.sort_order}</TableCell>
                    <TableCell>{formatTime(item.updated_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(item)} title="编辑">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(item.id)} title="删除">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle>{editing ? '编辑公告' : '新建公告'}</DialogTitle>
            <DialogContent>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                fullWidth
                label="标题（可选）"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                sx={{ mt: 1, mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="正文"
                required
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                type="number"
                label="排序（越小越优先）"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })}
                sx={{ mb: 1, width: 200 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                }
                label="启用"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>取消</Button>
              <Button type="submit" variant="contained">保存</Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </SidebarLayout>
  );
};

export default AnnouncementManagement;
